/**
 * Formatting functions for job data display and content generation.
 */

/**
 * Builds the public creator job URL from the creator username and job slug.
 * Consistent implementation for both frontend and backend.
 */
export function buildPublicJobUrl(
  username: string,
  slug?: string | null,
  options: { origin?: string | null } = {}
): string | undefined {
  const cleanUsernameValue = username.trim();
  const cleanSlug = slug?.trim();
  if (!cleanUsernameValue || !cleanSlug) return undefined;

  const origin = (options.origin?.trim() || "https://artha.link").replace(/\/+$/, "");
  const cleanUsername = cleanUsernameValue.startsWith("@") ? cleanUsernameValue : `@${cleanUsernameValue}`;
  return `${origin}/${cleanUsername}/jobs/${encodeURIComponent(cleanSlug)}`;
}

/**
 * Formats location fields (city, state, country) into a concise comma-separated label.
 */
export function formatJobLocation(job: {
  city?: string | null;
  state?: string | null;
  country?: string | null;
}): string | undefined {
  const parts = [job.city, job.state, job.country]
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
  const uniqueParts = [...new Set(parts)];
  return uniqueParts.length > 0 ? uniqueParts.join(", ") : undefined;
}

/**
 * Formats experience min/max fields and unit into a readable label (e.g., "2-5 years").
 */
export function formatJobExperience(job: {
  exp_min?: number | null;
  exp_max?: number | null;
  exp_unit?: string | null;
}): string | undefined {
  const unit = typeof job.exp_unit === "string" && job.exp_unit.trim() ? job.exp_unit.trim() : "years";
  const min = typeof job.exp_min === "number" ? job.exp_min : undefined;
  const max = typeof job.exp_max === "number" ? job.exp_max : undefined;

  if (min !== undefined && max !== undefined) return `${min}-${max} ${unit}`;
  if (min !== undefined) return `${min}+ ${unit}`;
  if (max !== undefined) return `Up to ${max} ${unit}`;
  return undefined;
}

/**
 * Adds prompt-only job fields such as public URL, location label, and experience label.
 * Used for preparing job data for LLM content generation prompts.
 */
export function buildPromptJob(job: Record<string, any>, username: string) {
  return {
    ...job,
    publicJobUrl: buildPublicJobUrl(username, job.slug),
    locationLabel: formatJobLocation(job) ?? "Location not specified",
    experienceLabel: formatJobExperience(job) ?? "Experience not specified",
  };
}
